import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import { AppDispatch } from "../../../redux/store";
import PermissionGate from "../../../core/layouts/permission-gate";
import ToastComponent from "../../../components/toast";
import {
  getErrorState,
  setBreadcrumbState,
} from "../../../redux/features/common-slice";
import { getUserState } from "../../../redux/features/auth-slice";
import {
  getUsersByTeamState,
  UserRole,
  usersByTeamStateAction,
  UserResponse,
} from "../../../redux/features/user-slice";
import Table from "../../../components/table";
import TableBodyCell from "../../../components/table-body-cell";
import EmptyDataOverlay from "../../../components/empty-data-overlay";
import paths from "../../../navigation/paths";
import Loading from "../../../components/loading";

export default function TeamLeaderPage() {
  const navigate = useNavigate();

  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector(getUserState).data;
  const usersByTeamState = useSelector(getUsersByTeamState);
  const errorState = useSelector(getErrorState);

  const fetchUsersByteam = useCallback(async () => {
    if (user) {
      await dispatch(usersByTeamStateAction({ teamId: user.category.id }));
    }
  }, [dispatch, user]);

  useEffect(() => {
    fetchUsersByteam();
  }, [fetchUsersByteam]);

  useEffect(() => {
    if (user) {
      dispatch(
        setBreadcrumbState([
          { id: "teamLeader", path: null, title: "Teams" },
          {
            id: `teamLeader-${user.team.id}`,
            path: null,
            title: user.team.name,
          },
        ])
      );
    }
  }, [dispatch, user]);

  useEffect(() => {
    if (errorState) {
      if (errorState.redirect) {
        navigate(errorState.redirect.destination, {
          replace: errorState.redirect.permanent,
        });
        return;
      }
      toast(errorState.message, { type: "error" });
    }
  }, [errorState]);

  return (
    <PermissionGate categories={[]} roles={[UserRole.Manager, UserRole.Leader]}>
      <Helmet title={user ? user.team.name : "Teams"} />
      <Table
        headerCells={[
          { id: "name", label: "Name", align: "text-left", width: "w-4/12" },
          {
            id: "position",
            label: "Position",
            align: "text-left",
            width: "w-3/12",
          },
          {
            id: "experience",
            label: "Experience",
            align: "text-left",
            width: "w-3/12",
          },
          {
            id: "role",
            label: "Role",
            align: "text-left",
            width: "w-2/12",
          },
        ]}
        body={
          usersByTeamState.loading
            ? [
                <tr key="loading-row">
                  <td colSpan={4} className="h-64">
                    <div className="flex flex-col items-center justify-center">
                      <Loading />
                    </div>
                  </td>
                </tr>,
              ]
            : usersByTeamState.data.length > 0
            ? usersByTeamState.data.map((member: UserResponse) => {
                return (
                  <tr
                    key={member.id}
                    className="border-t border-dashed border-divider cursor-pointer hover:bg-action-hover"
                    onClick={() => {
                      if (user) {
                        navigate(
                          `${paths.MEMBER}/${member.id}/${
                            member.category.id
                          }?tab=${encodeURIComponent(
                            "Daily Reports"
                          )}&tabType=${encodeURIComponent("Reports")}`
                        );
                      }
                    }}
                  >
                    <TableBodyCell exClassName="font-medium">
                      <label className="whitespace-normal break-words hover:underline cursor-pointer">
                        {member.name}
                      </label>
                      <br />
                      <label className="text-text-secondary text-xs font-light italic">
                        {member.email}
                      </label>
                    </TableBodyCell>
                    <TableBodyCell>
                      <label className="whitespace-normal break-words">
                        {member.position.name}
                      </label>
                    </TableBodyCell>
                    <TableBodyCell>
                      <label className="whitespace-normal break-words">
                        {member.experience.title}
                      </label>
                    </TableBodyCell>
                    <TableBodyCell>
                      <label className="whitespace-normal break-words capitalize">
                        {member.role}
                      </label>
                    </TableBodyCell>
                  </tr>
                );
              })
            : [
                <tr key="empty-row">
                  <td colSpan={4} className="h-64">
                    <div className="flex flex-col items-center justify-center">
                      <EmptyDataOverlay
                        label="No Users found"
                        exClassName="h-20"
                      />
                    </div>
                  </td>
                </tr>,
              ]
        }
      />

      <ToastComponent id="members" />
    </PermissionGate>
  );
}
